<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Berita', href: route('berita.index') },
            { name: 'Tambah Berita', href: '#' },
          ]"
        />

        <div class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <!-- Card header -->
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <h2 class="flex items-center gap-2 text-lg font-bold text-white">
              <i class="fa-solid fa-newspaper"></i>
              Tambah Berita
            </h2>
          </div>

          <form @submit.prevent="submit" class="p-6 space-y-6">

            <!-- Title -->
            <div>
              <InputLabel for="title" value="Judul Berita" class="mb-1.5" />
              <InputField
                id="title"
                icon="pen"
                v-model="form.title"
                placeholder="Masukkan judul berita..."
              />
              <InputError :message="form.errors.title" class="mt-1.5" />
            </div>

            <!-- Date · Status · Tags — 3 cols on md+ -->
            <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
              <div>
                <InputLabel for="publish_date" value="Tanggal Publish" class="mb-1.5" />
                <input
                  id="publish_date"
                  v-model="form.publish_date"
                  type="date"
                  class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                />
                <InputError :message="form.errors.publish_date" class="mt-1.5" />
              </div>

              <div>
                <InputLabel for="published" value="Status" class="mb-1.5" />
                <select
                  id="published"
                  v-model="form.published"
                  class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                  :class="{ 'border-red-500': form.errors.published }"
                >
                  <option value="">Pilih Status</option>
                  <option value="0">Draft</option>
                  <option value="1">Publish</option>
                </select>
                <InputError :message="form.errors.published" class="mt-1.5" />
              </div>

              <div>
                <InputLabel value="Kategori / Tags" class="mb-1.5" />
                <Multiselect
                  v-model="form.tags"
                  :options="props.allTags"
                  :multiple="true"
                  :taggable="true"
                  track-by="name"
                  label="name"
                  placeholder="Pilih atau buat tag"
                  tag-placeholder="Tambah tag baru"
                  @tag="addTag"
                />
                <InputError :message="form.errors.tags" class="mt-1.5" />
              </div>
            </div>

            <!-- Content editor — full width -->
            <div>
              <InputLabel value="Konten Berita" class="mb-1.5" />
              <div class="border border-gray-200 rounded-xl overflow-hidden">
                <QuillEditor
                  ref="quillRef"
                  v-model="form.content"
                  :error="form.errors.content"
                  :modules="modules"
                  class="quill-news"
                />
              </div>
              <InputError :message="form.errors.content" class="mt-1.5" />
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <a
                :href="route('berita.index')"
                class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Batal
              </a>
              <PrimaryBtn
                :disabled="form.processing"
                class="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i class="fa-solid fa-floppy-disk"></i>
                {{ form.processing ? "Menyimpan..." : "Simpan Berita" }}
              </PrimaryBtn>
            </div>

          </form>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { ref, onMounted, nextTick, inject } from "vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputField from "@/Components/InputField.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import QuillEditor from "@/Components/QuillEditor.vue";
import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";
import { router, useForm, usePage } from "@inertiajs/vue3";
import "vue-multiselect/dist/vue-multiselect.min.css";
import QuillResizeImage from "quill-resize-image";
import Quill from "quill";

const route = inject('route');

Quill.register("modules/resizeImage", QuillResizeImage);

const props = defineProps({
  news:    { type: Object, required: true },
  allTags: { type: Array,  required: true },
});

const quillRef = ref(null);

const form = useForm({
  title:        "",
  content:      "",
  publish_date: "",
  published:    "0",
  tags:         [],
});

function addTag(newTag) {
  const exists =
    form.tags.some((t) => t.name === newTag) ||
    props.allTags.some((t) => t.name === newTag);
  if (!exists) form.tags.push({ name: newTag });
}

// Resize image before upload (800×600 max)
function resizeImage(file, maxW = 800, maxH = 600) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width: w, height: h } = img;
      if (w / maxW > h / maxH) { if (w > maxW) { h = (h * maxW) / w; w = maxW; } }
      else                      { if (h > maxH) { w = (w * maxH) / h; h = maxH; } }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => resolve(b), file.type, 0.8);
    };
    img.src = URL.createObjectURL(file);
  });
}

function imageHandler() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/*";
  input.click();
  input.onchange = async () => {
    const file = input.files[0];
    if (!file) return;
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append("image", resized, file.name);
      const { data } = await axios.post("/berita/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await nextTick();
      const getQuill = () =>
        quillRef.value?.getQuill?.() ?? quillRef.value?.editor ?? null;
      const quill = getQuill();
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(range?.index ?? 0, "image", data.url);
      }
    } catch {
      Swal.fire({ title: "Gagal upload gambar", icon: "error" });
    }
  };
}

const modules = {
  toolbar: {
    container: [
      ["bold", "italic", "underline", "strike"],
      ["blockquote", "code-block"],
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      [{ color: [] }, { background: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
    handlers: { image: imageHandler },
  },
  resizeImage: {
    displaySize: true,
    modules: ["Resize", "DisplaySize", "Toolbar"],
    handleStyles: {
      backgroundColor: "#6366f1",
      border: "2px solid #fff",
      borderRadius: "50%",
      width: "12px",
      height: "12px",
    },
  },
};

onMounted(() => {
  const page = usePage();
  if (page.props.flash?.success) {
    Swal.fire({ title: "Berhasil!", text: page.props.flash.success, icon: "success" });
  }
});

const submit = async () => {
  // Client-side validation
  const errors = {};
  if (!form.title.trim())        errors.title        = "Judul harus diisi.";
  if (!form.content.trim())      errors.content      = "Konten harus diisi.";
  if (!form.publish_date)        errors.publish_date = "Tanggal publikasi harus diisi.";
  if (!form.published)           errors.published    = "Status publikasi harus diisi.";

  if (Object.keys(errors).length) {
    form.errors = errors;
    Swal.fire({ title: "Validasi Error", text: "Mohon lengkapi semua field yang diperlukan", icon: "error" });
    return;
  }

  const fd = new FormData();
  fd.append("title",        form.title);
  fd.append("content",      form.content);
  fd.append("publish_date", form.publish_date);
  fd.append("published",    form.published);
  form.tags.map((t) => t.name || t).forEach((t) => fd.append("tags[]", t));

  Swal.fire({ title: "Menyimpan...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  try {
    await axios.post(route("berita.store"), fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    await Swal.fire({ title: "Berhasil!", text: "Berita berhasil disimpan", icon: "success" });
    router.visit(route("berita.index"), { preserveScroll: true });
  } catch (error) {
    if (error.response?.status === 422) {
      form.errors = error.response.data.errors;
      Swal.fire({ title: "Validasi Gagal!", text: "Periksa kembali data yang diinputkan", icon: "error" });
    } else {
      Swal.fire({ title: "Error!", text: "Terjadi kesalahan saat menyimpan berita", icon: "error" });
    }
  }
};
</script>

<style scoped>
/* Quill editor full-height */
.quill-news {
  min-height: 450px;
  background: #fff;
}
.quill-news :deep(.ql-toolbar) {
  border: none;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0;
  padding: 8px 12px;
}
.quill-news :deep(.ql-container) {
  border: none;
  font-size: 1rem;
  min-height: 400px;
}
.quill-news :deep(.ql-editor) {
  min-height: 400px;
  padding: 16px 20px;
  line-height: 1.7;
}
.quill-news :deep(.ql-editor img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 10px auto;
  display: block;
  box-shadow: 0 1px 6px rgba(0,0,0,.1);
}

/* Multiselect */
:deep(.multiselect) {
  min-height: 40px;
  font-size: 0.875rem;
}
:deep(.multiselect__tags) {
  border-color: #d1d5db;
  border-radius: 0.5rem;
  padding: 6px 10px;
  min-height: 40px;
}
:deep(.multiselect__tag) {
  background: #6366f1;
  border-radius: 6px;
  padding: 3px 8px;
  margin: 2px 3px 2px 0;
}
:deep(.multiselect__option--highlight) { background: #6366f1; }
:deep(.multiselect__option--selected.multiselect__option--highlight) { background: #4338ca; }
:deep(.multiselect__input) { font-size: 0.875rem; }
</style>
